import { Injectable } from "@angular/core";
import {
  distinctUntilChanged,
  switchMap,
  filter,
  combineLatest,
  tap,
} from "rxjs/operators";
import { BehaviorSubject, Observable } from "rxjs";
import { UserHttpService } from "./user-http.service";
import { User } from "../core/data/user/user.model";
import { AuthenticationService } from "../core/authentication/authentication.service";
import { ErrorService } from "../core/error/error.service";
import { of } from "rxjs/observable/of";

@Injectable()
export class UserService {
  public usersList$: Observable<any>;
  public currentUser$: Observable<any>;
  private readonly usersListSubject: BehaviorSubject<
    any[]
  > = new BehaviorSubject([]);
  private readonly currentUserSubject: BehaviorSubject<
    any
  > = new BehaviorSubject(null);

  constructor(
    private userHttp: UserHttpService,
    private authService: AuthenticationService,
    private errorService: ErrorService
  ) {
    /**
     * We check the user status in the authentication service.
     * if the userStatus differ from unlogged it means that we
     * have login information in the storage so we can get
     * the current user. If the request succeed we can update
     * the currentuser subject and we can check teh real status
     * of the user and update in case it differs the one in the
     * authentication service.
     */
    this.usersList$ = this.usersListSubject
      .asObservable()
      .pipe(distinctUntilChanged());

    this.currentUser$ = this.currentUserSubject
      .asObservable()
      .pipe
      // distinctUntilChanged()
      ();

    this.authService.currentUser$.subscribe((user) => {
      console.log("User Changed");
      this.currentUserSubject.next(user);
    });

    this.authService.userUid$
      .pipe(
        filter((uid) => uid && true),
        combineLatest(this.authService.userStatus$, this.currentUser$),
        switchMap(([uid, status, user]) => {
          if (!user)
            return Observable.of(uid).pipe(
              combineLatest(Observable.of(status), this.getUser(uid, true))
            );
          return Observable.of([uid, status, user]);
        })
      )
      .subscribe(([uid, status, user]) => {
        if (status !== user.activeRoute) {
          this.authService.updateUserStatus(user.activeRoute);
        }
      });
  }

  updateUsersList(filters: any = {}) {
    this.userHttp
      .list(filters)
      .toPromise()
      .then((users: User[]) => {
        this.usersListSubject.next([...users]);
      });
  }

  getUserForce(authenticatedUser: any) {
    return this.userHttp
      .getForce(authenticatedUser)
      .toPromise()
      .then((user) => {
        if (user) this.authService.forceLogin(user, authenticatedUser.route);
        return user;
      })
      .catch((e) => {
        console.log("[CHECK USER AUTH ERROR]", JSON.stringify(e));
        this.logout();
      });
  }

  updateUser(userFileds: any = {}) {
    const prevUser = this.currentUserSubject.getValue();
    const obs$ = this.userHttp.update(prevUser._id, userFileds);
    obs$
      .catch((e) => {
        this.errorService.addError(e);
        return Observable.of(null);
      })
      .subscribe((user) => {
        if (user) {
          this.currentUserSubject.next(user);
          if (prevUser.activeRoute !== user.activeRoute) {
            this.authService.updateUserStatus(user.activeRoute);
          }
        }
      });
    return obs$;
  }

  updatePatient(id, filds): Promise<any> {
    return this.userHttp
      .update(id, filds)
      .toPromise()
      .catch((e) => console.error(e));
  }

  deletePatient(id): Promise<any> {
    return this.userHttp
      .deleteUserGroup(id)
      .toPromise()
      .catch((e) => console.error(e));
  }

  getUserGroupByUser(id): Promise<any> {
    return this.userHttp
      .getUserGroupByUser(id)
      .toPromise()
      .catch((e) => console.error(e));
  }

  updateUserListByGroup(groupId) {
    this.userHttp
      .getPartecipant(groupId)
      .toPromise()
      .then((users) => {
        this.usersListSubject.next(users);
      });
  }

  getUsersFromGroup(groupId) {
    return this.userHttp.getPartecipant(groupId).toPromise();
  }

  getListUsersGroup() {
    return this.userHttp
      .getUsersGroups()
      .toPromise()
      .catch((result) => {
        console.error("From Promise:", result);
      });
  }

  getUser(id: string, updateCurrent = false): Observable<User> {
    if (updateCurrent) {
      return this.userHttp.get(id).map((user) => {
        this.currentUserSubject.next(user);
        return user;
      });
    }
    this.currentUserSubject.next(this.getCurrentUser());
    return this.currentUser$;
  }

  updatePassword(oldPass: string, newPass: string) {
    const currentuser = this.getCurrentUser();
    const userid = currentuser && currentuser._id;
    if (oldPass && newPass && userid) {
      return this.userHttp
        .updatePassword(userid, oldPass, newPass)
        .toPromise()
        .catch((result) => {
          this.errorService.addError("CHANGE_PASSWORD_FAILED");
        });
    } else {
      this.errorService.addError("CHANGE_PASSWORD_FAILED");
    }
  }

  getCurrentUser() {
    return this.currentUserSubject.getValue();
  }

  logout() {
    let user = this.getCurrentUser();
    // return this.userHttp.logout(user).toPromise()
    //   .then(() => {
    //     this.currentUserSubject.next(null);
    //   })
    //   .catch(result => {
    //     this.currentUserSubject.complete();
    //     console.error('From Promise:', result);
    //   });

    if (user)
      return this.userHttp
        .logout(user)
        .toPromise()
        .then((result) => {
          this.currentUserSubject.next(null);
          return null;
        }); //.catch(result => {console.error('From Promise:', result);});

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/app/user/user.service.ts